import { useState, useEffect, useCallback, useMemo } from 'react'
import { Theme } from '../../constants/enums'
import { useUser } from '../../hooks/context/useUser'
import { 
  loadThemeFromCache, 
  saveThemeToCache 
} from '../../utils/userPreferencesCache'
import { updateUserTheme } from '../../apiClients/user/userClient'

/**
 * Определяет системную тему на основе prefers-color-scheme
 */
function getSystemTheme(): Theme.LIGHT | Theme.DARK {
  if (window.matchMedia('(prefers-color-scheme: light)').matches) {
    return Theme.LIGHT
  }
  return Theme.DARK
}

/**
 * Применяет тему к DOM
 */
function applyThemeToDOM(theme: Theme.LIGHT | Theme.DARK): void {
  const themeAttribute = theme === Theme.LIGHT ? 'light' : 'dark'
  document.documentElement.setAttribute('data-theme', themeAttribute)
}

/**
 * Хук для управления состоянием темы приложения
 * Управляет состоянием темы на основе:
 * 1. Выбора пользователя из localStorage
 * 2. Выбора пользователя с сервера (если залогинен)
 * 3. Системных настроек (если тема UNSPECIFIED)
 * 4. Темной темы по умолчанию
 */
export const useThemeState = () => {
  const { user, updateUser } = useUser()

  // actualTheme tracks the resolved theme (LIGHT or DARK) applied to the DOM
  // It's used internally but not exposed in the context to prevent unnecessary re-renders
  const [_actualTheme, setActualTheme] = useState<Theme.LIGHT | Theme.DARK>(getSystemTheme)
  
  /**
   * Инициализация темы: проверяем сервер, затем localStorage, затем UNSPECIFIED
   */
  const getInitialTheme = (): Theme => {
    // Приоритет 1: Тема авторизованного пользователя с сервера
    if (user?.themeId !== undefined && user.themeId !== Theme.UNSPECIFIED) {
      return user.themeId
    }
    
    // Приоритет 2: Тема из localStorage
    const cachedTheme = loadThemeFromCache()
    if (cachedTheme !== null && cachedTheme !== Theme.UNSPECIFIED) {
      return cachedTheme
    }
    
    // Приоритет 3: UNSPECIFIED (системная тема)
    return Theme.UNSPECIFIED
  }

  /**
   * Текущая тема пользователя (или UNSPECIFIED для системной)
   */
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)
  
  // Обновляем тему при изменении user.themeId (авторизация/выход)
  useEffect(() => {
    if (user?.themeId !== undefined && user.themeId !== Theme.UNSPECIFIED) {
      setThemeState(user.themeId)
    }
  }, [user?.themeId])

  /**
   * Применяет тему к DOM и обновляет состояние
   */
  const applyTheme = useCallback((newTheme: Theme.LIGHT | Theme.DARK) => {
    applyThemeToDOM(newTheme)
    setActualTheme(newTheme)
  }, [])

  /**
   * Эффект для синхронизации темы при изменении настроек пользователя
   * или системных настроек (для UNSPECIFIED)
   */
  useEffect(() => {
    const appliedTheme =
      theme === Theme.UNSPECIFIED ? getSystemTheme() : (theme as Theme.LIGHT | Theme.DARK)

    applyTheme(appliedTheme)

    // Подписываемся на изменения системной темы только для UNSPECIFIED
    if (theme === Theme.UNSPECIFIED) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handleChange = () => {
        applyTheme(getSystemTheme())
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme, applyTheme])

  /**
   * Устанавливает тему и обновляет профиль пользователя
   */
  const setTheme = useCallback(
    (newTheme: Theme) => {
      // Обновляем локальное состояние
      setThemeState(newTheme)
      
      // Всегда сохраняем в localStorage
      saveThemeToCache(newTheme)
      
      if (user) {
        updateUser({ themeId: newTheme })
        // Отправляем запрос на сервер (пока-заглушка)
        updateUserTheme(newTheme).catch((error) => {
          console.error("Failed to update user theme on server:", error)
        })
      } else {
        // Для незалогиненных пользователей применяем тему сразу
        const resolvedTheme =
          newTheme === Theme.UNSPECIFIED ? getSystemTheme() : (newTheme as Theme.LIGHT | Theme.DARK)
        applyTheme(resolvedTheme)
      }
    },
    [user, updateUser, applyTheme]
  )

  /**
   * Переключение между светлой и темной темами
   * Uses functional update to avoid dependency on actualTheme
   */
  const toggleTheme = useCallback(() => {
    // Calculate new theme by reading theme state, not actualTheme
    // If theme is UNSPECIFIED, we need to get the current system theme
    const currentResolvedTheme = theme === Theme.UNSPECIFIED ? getSystemTheme() : (theme as Theme.LIGHT | Theme.DARK)
    const newTheme = currentResolvedTheme === Theme.LIGHT ? Theme.DARK : Theme.LIGHT
    setTheme(newTheme)
  }, [theme, setTheme])

  // Синхронизация темы с сервера в localStorage при авторизации
  useEffect(() => {
    if (user?.themeId !== undefined && user.themeId !== Theme.UNSPECIFIED) {
      // Когда пользователь авторизуется, сохраняем его тему с сервера в localStorage
      const cachedTheme = loadThemeFromCache()
      if (cachedTheme !== user.themeId) {
        saveThemeToCache(user.themeId)
        console.log(
          `Synced theme from server (${Theme[user.themeId]}) to localStorage`
        )
      }
    }
  }, [user?.themeId])

  return useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  )
}
